// angular imports
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// devextreme imports
import { Column } from 'devextreme/ui/data_grid';

// 3rd party imports
import * as moment from 'moment-timezone';
import { Subscription } from 'rxjs';

// constant imports
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';
import { ActionConstants } from '../../../_constants/action-constants';

// component imports
import { ManageVendorAccountsComponent } from '../../../vendor/components/manage-vendor-accounts/manage-vendor-accounts.component';
import { WorkflowPaymentRequestLineItemEditorComponent } from '../workflow-payment-request-line-item-editor/workflow-payment-request-line-item-editor.component';

// service imports
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { DetailPopupsService } from '../../../cirabooks/shared-cirabooks-module/_services/detail-popups.service';
import { UserService } from '../../../_services/data-services/user.service';
import { WorkflowPaymentRequestService } from '../../_services/workflow-payment-request.service';

// model imports
import { WFVendorHistoryParametersDto } from '../../../shared-workflow-module/_models/wf-vendor-history-parameters-dto';
import { VendorAccountResultDto } from '../../../vendor/_models/vendor-account-result-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { WFParametersDto } from '../../../shared-workflow-module/_models/wf-parameter-dto';
import { WFEBankAccountResultDto } from '../../../shared-workflow-module/_models/wfe-bank-account-result-dto';
import { PaymentRequestCodingEditorParamterDto } from '../../_models/payment-request-coding-editor-parameter-dto';
import { PaymentRequestCodingEditorResultDto } from '../../_models/payment-request-coding-editor-result-dto';
import { PaymentRequestEditorResultDto } from '../../_models/payment-request-editor-result-dto';
import { PaymentRequestPayeeInfoParameterDto } from '../../_models/payment-request-payee-info-parameter-dto';
import { PaymentRequestPayeeInfoResultDto } from '../../_models/payment-request-payee-info-result-dto';
import { PaymentRequestRawDto } from '../../_models/payment-request-raw-dto';
import { WFEVendorResultDto } from '../../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { VendorLineItemTemplateRawDto } from '../../../shared-workflow-module/_models/vendor-lineItem-template-raw-dto';
import { VendorTemplateParameterDto } from '../../../shared-workflow-module/_models/vendor-template-parameter-dto';
import { VendorTemplateResultDto } from '../../../shared-workflow-module/_models/vendor-template-result-dto';
import { VendorAccountTemplateParameterDto } from '../../../shared-workflow-module/_models/vendor-account-template-parameter-dto';
import { VendorAccountTemplateResultDto } from '../../../shared-workflow-module/_models/vendor-account-template-result-dto';
import { WFEVendorAccountResultDto } from '../../../shared-workflow-module/_models/wfe-vendor-account-result-dto';

@Component({
    selector: 'app-workflow-payment-request-details',
    templateUrl: './workflow-payment-request-details.component.html',
    styleUrls: ['./workflow-payment-request-details.component.scss']
})
export class WorkflowPaymentRequestDetailsComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('manageVendorAccount', { static: false }) manageVendorAccount: ManageVendorAccountsComponent;
    @ViewChild('lineItemEditor', { static: false }) lineItemEditor: WorkflowPaymentRequestLineItemEditorComponent;
    @Input() community: CommunitySelectorResultDto;
    @Input() vendorCode: string;

    @Output() onVendorPayeeSelection = new EventEmitter<boolean>();
    @Output() isBoardApproved = new EventEmitter<boolean>();

    advancePaymentEditor: PaymentRequestEditorResultDto = new PaymentRequestEditorResultDto();
    codingEditor: PaymentRequestCodingEditorResultDto = new PaymentRequestCodingEditorResultDto();
    paymentWorkflow: PaymentRequestRawDto;

    bankAccountSource: Array<WFEBankAccountResultDto> = [];
    bankAccountColumns: Array<Column> = [];
    bankAccountBoxValue: Array<number> = [];
    selectedBankAccount: WFEBankAccountResultDto;

    currencyFormat: string;

    showPayeeSelector = false;
    payeeName: string;

    payeeInfo: PaymentRequestPayeeInfoResultDto = new PaymentRequestPayeeInfoResultDto();
    vendorAccountColumns: Array<Column> = [];
    vendorAccountBoxValue: Array<number> = [];

    showInvoiceDateInfo = false;
    showPrintAsInfo = false;
    showManageVendorAccountPopup = false;
    showAddEditVendorAccount = false;

    addEditVendorAccountPopUpTitle: string;
    vendorAccountConfigMode: string;

    selectedVendor: WFEVendorResultDto;
    selectedVendorAccountForEditor: VendorAccountResultDto;
    selectedVendorAccount: WFEVendorAccountResultDto;

    showVendorAccountHistory = false;
    vendorAccountHistoryBaseParams: WFVendorHistoryParametersDto;

    iconHistory = IconConstants.HISTORY;
    iconInfo = IconConstants.INFO;
    iconHelp = IconConstants.HELP;
    iconCross = IconConstants.CROSS_CIRCLE;
    iconEllipsish = IconConstants.ELLIPSISH;

    advancePaymentEditorPayeeHelpKey: string;
    detailsPopupSubscription: Subscription;
    isCorporateCirabook = false;

    defaultLineItems: Array<VendorLineItemTemplateRawDto> = [];

    isITUser = false;
    isInternalUser = false;
    isEFTDisabled = false;
    isVendorAccountMandatory = false;
    isLineItemEditorDisabled = true;
    vendorAccountDropDownButtonTemplate: Array<string>;

    autoPopulatedInvoiceNumber: string = null;

    loadingVisible = false;

    constructor(
        private readonly workflowPaymentRequestService: WorkflowPaymentRequestService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly userService: UserService
    ) {
        this.onPayeeClick = this.onPayeeClick.bind(this);
        this.onPayeeClear = this.onPayeeClear.bind(this);
    }

    ngOnInit() {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.isITUser = this.userService.isITUser();
        this.isInternalUser = this.userService.isInternal();
        this.vendorAccountDropDownButtonTemplate = Utils.isTrue(this.isInternalUser) ? ['dropDownDetailButton'] : [];

        this.detailsPopupSubscription = this.workflowSharedService.getDetailsParams().subscribe((params: any) => {
            const paymentId = Utils.notNullAndDefined(params.paymentId) ? parseInt(params.paymentId) : null;
            const communityId = Utils.notNullAndDefined(params.communityId) ? parseInt(params.communityId) : null;
            const managementCompanyId = Utils.notNullAndDefined(params.managementCompanyId)
                ? parseInt(params.managementCompanyId)
                : null;

            if (
                Utils.notNullAndDefined(paymentId) &&
                Utils.notNullAndDefined(communityId) &&
                Utils.notNullAndDefined(managementCompanyId)
            ) {
                this.detailPopupsService.updateSubjectAPPaymentDetail(managementCompanyId, communityId, paymentId);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (Utils.notNullAndDefined(changes.community) && Utils.notNullAndDefined(changes.community.currentValue)) {
            this.community = changes.community.currentValue;
            if (Utils.notNullAndDefined(this.community)) {
                this.resetDetails();
                setTimeout(() => {
                    this.getAdvanceEditor();
                }, 200);
            } else {
                this.resetDetails();
            }
        }
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.detailsPopupSubscription)) {
            this.detailsPopupSubscription.unsubscribe();
        }
    }

    onPayeeClick(): void {
        this.showPayeeSelector = true;
    }

    onPayeeHelpClick(): void {
        const helpLink = this.ciraHelpService.getHelpLinkByKey(
            this.advancePaymentEditorPayeeHelpKey,
            this.community.communityId
        );
        this.ciraHelpService.showHelp(helpLink);
    }

    onPayeeSelection(event: any): void {
        if (Utils.notNullAndDefined(event)) {
            this.resetPayee();
            this.paymentWorkflow.payeeCode = event.payeeCode;
            this.paymentWorkflow.payeeType = event.payeeType;
            this.payeeName = event.payeeName;
            this.getCodingEditor();

            if (Utils.equalsIgnoreCase(this.paymentWorkflow.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR)) {
                this.getVendorAccountColumns();
                this.selectedVendor = event.payee;
                this.onVendorPayeeSelection.emit(event.payee);

                // As per DEV-3846, setting EFT and access for selected Payee EFT
                this.setWorkflowEFT(this.selectedVendor.isEFTVendor);
                this.modifyWorfkflowEFTAccess(this.selectedVendor.isEFTVendor);

                setTimeout(() => {
                    this.getPayeeInfo();
                }, 100);

                if (Utils.isTrue(this.selectedVendor.isActive)) {
                    this.isVendorAccountMandatory = Utils.isInList(this.selectedVendor.vendorTypeCode, ['A', 'I', 'U']);
                    this.paymentWorkflow.isVendorAccountMandatory = this.isVendorAccountMandatory;
                }

                if (Utils.isFalse(this.selectedVendor.isBranchVendor)) {
                    this.getVendorTemplate(true);
                }
            } else {
                this.onVendorPayeeSelection.emit(null);
                this.populateInvoiceNumber();
            }

            if (Utils.equalsIgnoreCase(this.paymentWorkflow.payeeType, WorkflowConstants.PAYEE_TYPE_OWNER)) {
                setTimeout(() => {
                    this.getPayeeInfo();
                }, 100);
            }
        }
        this.checkLineItemEditorStatus();
    }

    onPayeeSelectorClose(isPayeeSelected: boolean): void {
        this.showPayeeSelector = false;

        if (Utils.isFalse(isPayeeSelected)) {
            this.payeeName = null;
        }
    }

    onBankAccountSelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedBankAccount = selectedObject.selectedRow[0];
            if (Utils.notEquals(selectedBankAccount.bankAccountId, this.paymentWorkflow.bankAccountId)) {
                this.paymentWorkflow.bankAccountId = selectedBankAccount.bankAccountId;
                this.bankAccountBoxValue = [this.paymentWorkflow.bankAccountId];
                this.selectedBankAccount = selectedBankAccount;
                this.getCodingEditor();
            }
        } else {
            this.paymentWorkflow.bankAccountId = null;
            this.selectedBankAccount = null;
        }
        this.checkLineItemEditorStatus();
    }

    onVendorAccountSelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            this.selectedVendorAccount = selectedObject.selectedRow[0];
            this.paymentWorkflow.vendorAccountId = this.selectedVendorAccount.id;
            this.setWorkflowEFT(this.selectedVendorAccount.eftActive);
            this.getVendorAccountTemplate(false);
            this.vendorAccountDropDownButtonTemplate = ['dropDownDetailButton', 'dropDownClearButton'];
        } else {
            this.selectedVendorAccount = null;
            this.paymentWorkflow.vendorAccountId = null;
            if (!!this.selectedVendor) {
                this.setWorkflowEFT(this.selectedVendor.isEFTVendor);
                this.modifyWorfkflowEFTAccess(this.selectedVendor.isEFTVendor);
                if (Utils.isFalse(this.selectedVendor.isBranchVendor)) {
                    this.getVendorTemplate(false);
                }
            }
            this.vendorAccountDropDownButtonTemplate = ['dropDownDetailButton'];
        }
        this.populateInvoiceNumber();
        this.checkLineItemEditorStatus();
    }

    onVendorAccountDetailClick(): void {
        this.showManageVendorAccountPopup = true;
    }

    onManageVendorAccountClose(): void {
        this.showManageVendorAccountPopup = false;

        this.getPayeeInfo();
    }

    onVendorAccountSelectionForEditor(event: any): void {
        this.selectedVendorAccountForEditor = event;
        if (Utils.notNullAndDefined(this.selectedVendorAccountForEditor)) {
            this.addEditVendorAccountPopUpTitle = 'Edit Vendor Account';
            this.vendorAccountConfigMode = ActionConstants.EDIT;
        } else {
            this.addEditVendorAccountPopUpTitle = 'Add Vendor Account';
            this.vendorAccountConfigMode = ActionConstants.ADD;
        }
        this.showAddEditVendorAccount = true;
        this.showManageVendorAccountPopup = false;
    }

    onVendorAccountEditorClose(event: any): void {
        this.showAddEditVendorAccount = false;
        this.showManageVendorAccountPopup = true;

        if (Utils.notNullAndDefined(event)) {
            if (Utils.notNullAndDefined(this.selectedVendorAccountForEditor)) {
                setTimeout(() => this.manageVendorAccount.refreshVendorAccountNotes(), 250);
                setTimeout(() => this.manageVendorAccount.refreshVendorAccountHistory(), 250);
            }

            setTimeout(() => {
                this.manageVendorAccount.reloadManageAccounts();
            }, 250);
        }
    }

    onInvoiceDateInfoClick(): void {
        this.showInvoiceDateInfo = true;
    }

    onPrintAsInfoInfoClick(): void {
        this.showPrintAsInfo = true;
    }

    onInvoiceAmountChanged(): void {
        this.getCodingEditor();
        this.checkLineItemEditorStatus();
    }

    onVendorAccountHistoryClick(): void {
        if (Utils.notNullAndDefined(this.paymentWorkflow.vendorAccountId)) {
            this.vendorAccountHistoryBaseParams = new WFVendorHistoryParametersDto();
            this.vendorAccountHistoryBaseParams.communityId = this.community.communityId;
            this.vendorAccountHistoryBaseParams.vendorAccountId = this.paymentWorkflow.vendorAccountId;
            this.vendorAccountHistoryBaseParams.vendorCode = this.paymentWorkflow.payeeCode;
            this.showVendorAccountHistory = true;
        }
    }

    onVendorAccountHistoryClose(): void {
        this.showVendorAccountHistory = false;
    }

    onPayeeClear(): void {
        this.resetPayee();
        this.populateInvoiceNumber();
    }

    onAddVendorAccountClick(): void {
        this.vendorAccountConfigMode = ActionConstants.ADD;
        this.addEditVendorAccountPopUpTitle = 'Add Vendor Account';
        this.showAddEditVendorAccount = true;
    }

    onInvoiceDateChanged(): void {
        this.populateInvoiceNumber();
    }

    checkLineItemEditorStatus(): void {
        if (
            Utils.isNullOrEmpty(this.paymentWorkflow.payeeType) ||
            Utils.isNullOrEmpty(this.paymentWorkflow.bankAccountId) ||
            Utils.isNullOrEmpty(this.paymentWorkflow.invoiceAmount)
        ) {
            this.isLineItemEditorDisabled = true;
        } else if (
            Utils.isTrue(this.isVendorAccountMandatory) &&
            Utils.isNullOrEmpty(this.paymentWorkflow.vendorAccountId)
        ) {
            this.isLineItemEditorDisabled = true;
        } else {
            this.isLineItemEditorDisabled = false;
        }
    }

    private populateInvoiceNumber(): void {
        const newAutoPopulatedInvoiceNumber = this.generateInvoiceNumber();
        if (
            Utils.notEqualsIgnoreCase(newAutoPopulatedInvoiceNumber, this.autoPopulatedInvoiceNumber) &&
            (Utils.equalsIgnoreCase(this.paymentWorkflow.invoiceNumber, this.autoPopulatedInvoiceNumber) ||
                Utils.isNullOrEmpty(this.paymentWorkflow.invoiceNumber))
        ) {
            // Adding setTimeout to avoid expression change in few scenario's
            setTimeout(() => (this.paymentWorkflow.invoiceNumber = newAutoPopulatedInvoiceNumber), 50);
        }
        this.autoPopulatedInvoiceNumber = newAutoPopulatedInvoiceNumber;
    }

    private generateInvoiceNumber(): string {
        let generatedInvoiceNumber = null;

        if (Utils.notNullOrEmpty(this.paymentWorkflow.invoiceDate)) {
            const formattedInvoiceDate = moment(this.paymentWorkflow.invoiceDate).format('MMYY');
            if (
                Utils.equalsIgnoreCase(this.paymentWorkflow.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR) &&
                Utils.notNullAndDefined(this.selectedVendor) &&
                Utils.isInList(this.selectedVendor.vendorTypeCode, ['A', 'U']) &&
                Utils.notNullAndDefined(this.selectedVendorAccount)
            ) {
                // replacing all occurence of '-' and ' ' with '' from selectedVendorAccount.accountNumber.
                const formattedVendorAccountNumber = this.selectedVendorAccount.accountNumber
                    .replace(/-/g, '')
                    .replace(/ /g, '')
                    .trim();
                generatedInvoiceNumber = `${formattedVendorAccountNumber}-${formattedInvoiceDate}`;
            } else {
                generatedInvoiceNumber = formattedInvoiceDate;
            }
        }

        return generatedInvoiceNumber;
    }

    private setWorkflowEFT(isEFT: boolean): void {
        this.paymentWorkflow.isEFTAccount = isEFT;
    }

    private modifyWorfkflowEFTAccess(isEFT: boolean): void {
        this.isEFTDisabled = isEFT && !this.isITUser;
    }

    private getVendorTemplate(clearCache: boolean): void {
        const baseParams: VendorTemplateParameterDto = new VendorTemplateParameterDto();
        baseParams.communityId = this.community.communityId;
        baseParams.vendorCode = this.selectedVendor.vendorCode;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;
        this.workflowPaymentRequestService.getVendorLineTemplate(baseParams).subscribe(
            (result: VendorTemplateResultDto) => {
                this.defaultLineItems = [];
                if (Utils.notNullAndDefined(result.headerTemplate)) {
                    this.paymentWorkflow.description = Utils.notNullAndDefined(result.headerTemplate.description)
                        ? result.headerTemplate.description
                        : null;
                }
                if (Utils.isFalse(result.hasAccountLevelTemplates) && Utils.notNullOrEmpty(result.lineItemTemplates)) {
                    this.defaultLineItems = result.lineItemTemplates.filter((d) => Utils.isTrue(d.enabled));
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getVendorAccountTemplate(clearCache: boolean): void {
        const baseParams: VendorAccountTemplateParameterDto = new VendorAccountTemplateParameterDto();
        baseParams.communityId = this.community.communityId;
        baseParams.vendorCode = this.selectedVendor.vendorCode;
        baseParams.vendorAccountId = this.selectedVendorAccount.id;
        baseParams.clearCache = clearCache;

        this.workflowPaymentRequestService.getVendorAccountLineTemplate(baseParams).subscribe(
            (result: VendorAccountTemplateResultDto) => {
                if (result.headerTemplate) {
                    this.paymentWorkflow.description = Utils.notNullAndDefined(result.headerTemplate.description)
                        ? result.headerTemplate.description
                        : null;
                }
                if (Utils.notNullOrEmpty(result.lineItemTemplates)) {
                    this.defaultLineItems = result.lineItemTemplates.filter((d) => Utils.isTrue(d.enabled));
                } else {
                    this.getVendorTemplate(false);
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.getVendorTemplate(false);
            }
        );
    }

    private getAdvanceEditor(clearCache = false): void {
        if (Utils.notNullAndDefined(this.community)) {
            const baseParams: WFParametersDto = new WFParametersDto();
            baseParams.communityId = this.community.communityId;
            baseParams.clearCache = clearCache;

            this.loadingVisible = true;
            this.workflowPaymentRequestService.getAdvanceEditor(baseParams).subscribe(
                (result: PaymentRequestEditorResultDto) => {
                    this.advancePaymentEditor = result;
                    this.paymentWorkflow = result.workflow;
                    this.paymentWorkflow.generatePaymentRequest = true;
                    this.paymentWorkflow.communityId = this.community.communityId;
                    this.paymentWorkflow.invoiceAmount = Utils.notNullOrEmpty(this.paymentWorkflow.invoiceAmount)
                        ? this.paymentWorkflow.invoiceAmount
                        : 0;

                    this.isBoardApproved.emit(this.advancePaymentEditor.boardApproval);

                    this.advancePaymentEditorPayeeHelpKey = this.ciraHelpService.getHelpLinkByKey(
                        this.advancePaymentEditor.payeeHelpKey,
                        this.community.communityId
                    );
                    this.prepareEditor();
                    this.populateInvoiceNumber();
                    this.prepareBankSource();

                    if (Utils.notNullAndDefined(this.vendorCode)) {
                        this.searchVendor();
                    }

                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    private prepareEditor(): void {
        if (Utils.isNullOrEmpty(this.paymentWorkflow.invoiceDate)) {
            this.paymentWorkflow.invoiceDate = new Date();
        }
    }

    private getCodingEditor(): void {
        if (
            Utils.notNullAndDefined(this.paymentWorkflow.bankAccountId) &&
            Utils.notNullAndDefined(this.paymentWorkflow.payeeCode) &&
            Utils.notNullAndDefined(this.paymentWorkflow.invoiceAmount)
        ) {
            const baseParams: PaymentRequestCodingEditorParamterDto = new PaymentRequestCodingEditorParamterDto();
            baseParams.communityId = this.community.communityId;
            baseParams.bankAccountId = this.paymentWorkflow.bankAccountId;
            baseParams.payeeCode = this.paymentWorkflow.payeeCode;

            this.workflowPaymentRequestService.getAdvanceCodingEditor(baseParams).subscribe(
                (result: PaymentRequestCodingEditorResultDto) => {
                    this.codingEditor = result;
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    private getPayeeInfo(): void {
        const baseParams: PaymentRequestPayeeInfoParameterDto = new PaymentRequestPayeeInfoParameterDto();
        baseParams.communityId = this.community.communityId;
        baseParams.payeeCode = this.paymentWorkflow.payeeCode;

        this.workflowPaymentRequestService.getPayeeInfo(baseParams).subscribe(
            (result: PaymentRequestPayeeInfoResultDto) => {
                this.payeeInfo = result;
                this.paymentWorkflow.printedOwnerName = null;

                if (Utils.equalsIgnoreCase(this.paymentWorkflow.payeeType, WorkflowConstants.PAYEE_TYPE_OWNER)) {
                    this.paymentWorkflow.printedOwnerName = Utils.notNullAndDefined(this.payeeInfo.printOwnerName)
                        ? this.payeeInfo.printOwnerName
                        : this.payeeName;
                }

                if (
                    Utils.notNullAndDefined(this.selectedVendor) &&
                    this.selectedVendor.vendorBranchAssociationId > 0 &&
                    Utils.isFalse(this.selectedVendor.isBranchVendor) &&
                    this.isInternalUser
                ) {
                    this.vendorAccountDropDownButtonTemplate = Utils.notNullOrEmpty(this.payeeInfo.vendorAccounts)
                        ? ['dropDownDetailButton', 'dropDownClearButton']
                        : ['dropDownAddButton'];
                }

                if (
                    Utils.notNullOrEmpty(this.payeeInfo.vendorAccounts) &&
                    Utils.equals(this.payeeInfo.vendorAccounts.length, 1)
                ) {
                    this.selectedVendorAccount = this.payeeInfo.vendorAccounts[0];
                    this.vendorAccountBoxValue = [this.payeeInfo.vendorAccounts[0].id];
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private prepareBankSource(): void {
        const bankAccountSource: Array<WFEBankAccountResultDto> = [];

        this.advancePaymentEditor.bankAccounts.forEach((account) => {
            let obj: any = {};
            obj = Object.assign(obj, account);
            obj.glDimensions = [];
            if (Utils.notNullOrEmpty(account.glDimensions)) {
                account.glDimensions.forEach((dim) => {
                    obj.glDimensions.push({
                        dimensionTitle: dim.dimensionTitle,
                        dimensionOptionTitle: dim.dimensionOptionTitle,
                        dimensionOptionCode: dim.dimensionOptionCode,
                        dimensionCode: dim.dimensionCode
                    });
                });
            }
            if (Utils.isTrue(account.isAPDefault)) {
                this.bankAccountBoxValue = [account.bankAccountId];
                this.selectedBankAccount = account;
            }
            bankAccountSource.push(obj);
        });

        this.bankAccountSource = bankAccountSource;
        if (Utils.equals(this.bankAccountSource.length, 1) && Utils.isNullOrEmpty(this.bankAccountBoxValue)) {
            this.bankAccountBoxValue = [this.bankAccountSource[0].bankAccountId];
            this.selectedBankAccount = this.bankAccountSource[0];
        }
        this.prepareBankAccountColumns();
    }

    private searchVendor(): void {
        const baseParams: WFParametersDto = new WFParametersDto();
        baseParams.communityId = this.community.communityId;

        this.loadingVisible = true;

        this.workflowPaymentRequestService.getVendors(baseParams).subscribe(
            (result: WFEVendorResultDto[]) => {
                const selectedVendor = result.find((d) => Utils.equalsIgnoreCase(this.vendorCode, d.vendorCode));
                if (Utils.notNullAndDefined(selectedVendor)) {
                    this.onPayeeSelection({
                        payeeCode: selectedVendor.vendorCode,
                        payeeName: selectedVendor.vendorName,
                        payeeType: WorkflowConstants.PAYEE_TYPE_VENDOR,
                        payee: selectedVendor
                    });
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private prepareBankAccountColumns(): void {
        this.bankAccountColumns = [
            {
                dataField: 'accountName',
                dataType: 'string',
                caption: 'Bank Account'
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account Number'
            },
            {
                dataType: 'string',
                caption: 'GL Dimensions',
                cellTemplate: 'multiDimensionsTemplate'
            },
            {
                dataField: 'isDefault',
                dataType: 'boolean',
                caption: 'isDefault',
                cellTemplate: 'booleanValueCellTemplate'
            }
        ];
    }

    private getVendorAccountColumns(): void {
        this.vendorAccountColumns = [
            {
                dataType: 'string',
                dataField: 'accountNumber',
                caption: 'Account Number'
            },
            {
                dataType: 'string',
                dataField: 'description',
                caption: 'Description'
            },
            {
                dataType: 'boolean',
                dataField: 'eftActive',
                caption: 'EFT',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];
    }

    private resetPayee(): void {
        this.vendorAccountBoxValue = [];
        this.codingEditor = null;
        this.selectedVendorAccountForEditor = null;
        this.payeeName = null;
        this.selectedVendor = null;
        this.selectedVendorAccount = null;
        this.defaultLineItems = [];
        this.payeeInfo = null;
        this.isEFTDisabled = false;
        this.isVendorAccountMandatory = false;

        if (Utils.notNullAndDefined(this.paymentWorkflow)) {
            this.paymentWorkflow.printedOwnerName = null;
            this.paymentWorkflow.payeeCode = null;
            this.paymentWorkflow.payeeType = null;
            this.paymentWorkflow.description = null;
            this.paymentWorkflow.vendorAccountId = null;
            this.paymentWorkflow.printedOwnerName = null;
            this.paymentWorkflow.isEFTAccount = false;
            this.paymentWorkflow.items = [];
        }
        this.checkLineItemEditorStatus();
        this.onVendorPayeeSelection.emit(null);
    }

    public resetDetails(): void {
        this.paymentWorkflow = new PaymentRequestRawDto();
        this.bankAccountSource = [];
        this.bankAccountBoxValue = [];
        this.selectedBankAccount = null;
        this.resetPayee();
    }

    public getAdvancePaymentRequest(): PaymentRequestRawDto {
        if (Utils.notNullAndDefined(this.paymentWorkflow)) {
            if (Utils.notNullAndDefined(this.lineItemEditor)) {
                this.paymentWorkflow.items = this.lineItemEditor.getModifiedLineItems();
                return this.paymentWorkflow;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    public getAdvancePaymentRequestDetail(): PaymentRequestRawDto {
        if (Utils.notNullAndDefined(this.paymentWorkflow)) {
            return this.paymentWorkflow;
        } else {
            return null;
        }
    }

    public isLineItemsValid(): boolean {
        return this.lineItemEditor.isValidLineItems();
    }
}
